// Lost Deals Finder — agent page: kick off inbox scans, browse findings.
//
// Reached from the Agents catalog card. Results tab shows the classified
// findings table for the selected run (CSV-downloadable via
// window.exportCsv); Runs tab is the per-user run history. Runs execute on
// the agent worker — we poll the runs list while one is queued/running
// (same interval pattern as BulkFillPanel in settings_panes.jsx).

const LD_RELEVANCE_HUES = {
  seller_opportunity: 'green',
  buyer_interest: 'teal',
  referral_partner: 'violet',
  other: 'slate',
  unclassified: 'slate',
  recruiting: 'amber',
  service_vendor: 'amber',
  personal: 'rose',
};

const LD_CATEGORY_LABELS = {
  unknown_contact: 'Unknown contact',
  unanswered_inbound: 'Unanswered inbound',
  gone_quiet: 'Gone quiet',
};

const ldLabel = (s) => (s || '').replace(/_/g, ' ');

// Small modal for the act-with-note flows — the note is required for both:
// dismiss feeds admin review + prompt tuning, check tells the team what was
// done about a real finding.
const LD_NOTE_COPY = {
  dismiss: {
    title: 'Mark as false positive',
    prompt: 'why is this not a real finding? The explanation is saved for admin review.',
    placeholder: 'e.g. This is our own IT vendor, not a deal contact',
    confirm: 'Dismiss finding',
  },
  check: {
    title: 'Mark as checked',
    prompt: 'what was done about it? The note is saved so the team knows how it was handled.',
    placeholder: 'e.g. Called him back — deal is now tracked in Atlas',
    confirm: 'Mark checked',
  },
};

const LdNoteModal = ({ row, action, onCancel, onConfirm, busy }) => {
  const [reason, setReason] = React.useState('');
  const copy = LD_NOTE_COPY[action];
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onCancel}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 18, width: 440, boxShadow: 'var(--shadow-sm)' }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>{copy.title}</div>
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 10 }}>
          {row.company_guess || row.contact_name || row.contact_email} — {copy.prompt}
        </div>
        <textarea autoFocus value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
          placeholder={copy.placeholder}
          style={{ width: '100%', fontSize: 12.5, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface-2)', color: 'var(--text)', padding: 8, resize: 'vertical' }} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <Btn size="sm" variant="outline" onClick={onCancel}>Cancel</Btn>
          <Btn size="sm" variant="accent" disabled={!reason.trim() || busy}
            onClick={() => onConfirm(reason.trim())}>{busy ? 'Saving…' : copy.confirm}</Btn>
        </div>
      </div>
    </div>
  );
};

// Proposed follow-up draft viewer — copy it or hand it to the mail client;
// Atlas never sends mail (and the Graph scope is read-only anyway).
const LdDraftModal = ({ row, onClose }) => {
  const [text, setText] = React.useState(row.draft_reply || '');
  const mailto = `mailto:${encodeURIComponent(row.contact_email || '')}`
    + `?subject=${encodeURIComponent('RE: ' + (row.subject || ''))}`
    + `&body=${encodeURIComponent(text)}`;
  // On a phone or iPad, window.open leaves a blank tab behind in Safari, so
  // hand the mailto: off in place (the native shell routes it to Mail).
  // Desktop keeps opening it the way it always has.
  const openMail = () => {
    const d = window.AtlasDevice;
    if (d && d.touch) {
      if (window.AtlasNative && window.AtlasNative.openExternal) window.AtlasNative.openExternal(mailto);
      else window.location.href = mailto;
      return;
    }
    window.open(mailto, '_blank');
  };
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 18, width: 520, boxShadow: 'var(--shadow-sm)' }}
        onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>Proposed follow-up</div>
        <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginBottom: 10 }}>
          To {row.contact_name || row.contact_email} · re: {row.subject} — review and edit before sending.
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8}
          style={{ width: '100%', fontSize: 12.5, lineHeight: 1.5, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface-2)', color: 'var(--text)', padding: 10, resize: 'vertical' }} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <Btn size="sm" variant="outline" onClick={onClose}>Close</Btn>
          <Btn size="sm" variant="outline" onClick={() => {
            navigator.clipboard.writeText(text).then(() => pushToast({ type: 'success', title: 'Draft copied' }));
          }}>Copy</Btn>
          <Btn size="sm" variant="accent" onClick={openMail}>Open in email</Btn>
        </div>
      </div>
    </div>
  );
};

const LostDealsView = ({ goToAgents, goToCompany }) => {
  const [runs, setRuns] = React.useState(null);
  const [tab, setTab] = React.useState('results');
  const [selectedId, setSelectedId] = React.useState(null);
  const [results, setResults] = React.useState(null);
  const [days, setDays] = React.useState(90);
  const [customRange, setCustomRange] = React.useState(false);
  const [sinceDate, setSinceDate] = React.useState('');
  const [untilDate, setUntilDate] = React.useState('');
  const [starting, setStarting] = React.useState(false);
  const [relevanceFilter, setRelevanceFilter] = React.useState('all');

  const loadRuns = React.useCallback(async () => {
    try {
      const rows = await window.AtlasApi.lostDealsRuns();
      setRuns(rows || []);
      return rows || [];
    } catch (_) { setRuns([]); return []; }
  }, []);

  React.useEffect(() => { loadRuns(); }, [loadRuns]);

  // Default selection: newest completed run.
  React.useEffect(() => {
    if (selectedId || !runs || !runs.length) return;
    const done = runs.find((r) => r.status === 'completed');
    if (done) setSelectedId(done.id);
  }, [runs, selectedId]);

  // Poll while anything is in flight; when the in-flight run completes,
  // select it so the fresh results appear without a click. A scan runs for
  // minutes, so the ladder backs off from 2.5s as it ages and the hook sits
  // out any stretch where the tab is hidden.
  const inFlight = (runs || []).some((r) => r.status === 'queued' || r.status === 'running');
  window.usePolling(
    React.useCallback(async () => {
      const rows = await loadRuns();
      const stillGoing = rows.some((r) => r.status === 'queued' || r.status === 'running');
      if (!stillGoing) {
        const newest = rows.find((r) => r.status === 'completed');
        if (newest) { setSelectedId(newest.id); setTab('results'); }
        const failed = rows.find((r) => r.status === 'failed');
        if (!rows.some((r) => r.status === 'completed') && failed) {
          pushToast({ type: 'error', title: 'Scan failed', body: failed.output_summary || 'See run history.' });
        }
      }
      return rows;
    }, [loadRuns]),
    {
      active: inFlight,
      first: 2500,
      // Statuses only — the run rows carry timestamps that churn without the
      // scan having actually moved on.
      signature: (rows) => (rows || []).map((r) => `${r.id}:${r.status}`).join(','),
    },
  );

  React.useEffect(() => {
    if (!selectedId) { setResults(null); return; }
    let dead = false;
    setResults(null);
    window.AtlasApi.lostDealsResults(selectedId)
      .then((res) => { if (!dead) setResults(res); })
      .catch(() => { if (!dead) setResults({ columns: [], rows: [], row_count: 0, status: 'failed' }); });
    return () => { dead = true; };
  }, [selectedId]);

  const startRun = async () => {
    if (customRange && !sinceDate) {
      pushToast({ type: 'warning', title: 'Pick a start date', body: 'A custom range needs at least a From date.' });
      return;
    }
    setStarting(true);
    try {
      const payload = customRange
        ? { since: sinceDate, until: untilDate || undefined }
        : { days };
      await window.AtlasApi.createLostDealsRun(payload);
      const windowText = customRange
        ? `${sinceDate} → ${untilDate || 'now'}`
        : `the last ${days} days`;
      pushToast({ type: 'info', title: 'Scan started', body: `Scanning ${windowText} of your inbox.` });
      loadRuns();
    } catch (e) {
      // apiFetch throws "400 Bad Request: {json}" — surface just the detail.
      let msg = (e && e.message) || 'Unknown error';
      try {
        const m = msg.match(/\{.*\}$/s);
        const parsed = m && JSON.parse(m[0]);
        if (parsed && parsed.detail) msg = parsed.detail;
      } catch (_) { /* keep raw message */ }
      pushToast({ type: 'error', title: 'Could not start the scan', body: msg });
    } finally {
      setStarting(false);
    }
  };

  const [statusFilter, setStatusFilter] = React.useState('active');
  const [noteTarget, setNoteTarget] = React.useState(null); // {row, action: 'check'|'dismiss'}
  const [draftTarget, setDraftTarget] = React.useState(null);
  const [actionBusy, setActionBusy] = React.useState(false);

  // Cross-run findings registry — backs the Checked / Dismissed / Imported
  // tabs. Acted-on findings live here forever (re-scans refresh their data),
  // so they survive new scans instead of vanishing with the run view.
  const [registry, setRegistry] = React.useState(null);
  const loadRegistry = React.useCallback(() => {
    window.AtlasApi.lostDealsFindings()
      .then((rows) => setRegistry(rows || []))
      .catch(() => setRegistry([]));
  }, []);
  React.useEffect(() => { loadRegistry(); }, [loadRegistry, selectedId]);

  // Weekly-scan opt-in lives on the user's Microsoft credential features
  // (enabled_features.lost_deals_weekly) — the worker queues a scan weekly
  // and delivers new findings as a follow-up task.
  const [msFeatures, setMsFeatures] = React.useState(null);
  React.useEffect(() => {
    window.AtlasApi.listIntegrations()
      .then((creds) => {
        const ms = (creds || []).find((c) => c.provider === 'microsoft' && c.active);
        setMsFeatures(ms ? (ms.enabled_features || {}) : null);
      })
      .catch(() => setMsFeatures(null));
  }, []);
  const weeklyOn = !!(msFeatures && msFeatures.lost_deals_weekly);
  const toggleWeekly = async () => {
    if (!msFeatures) return;
    const next = { ...msFeatures, lost_deals_weekly: !weeklyOn };
    try {
      const cred = await window.AtlasApi.updateIntegrationFeatures('microsoft', next);
      setMsFeatures(cred.enabled_features || next);
      pushToast({
        type: 'success',
        title: !weeklyOn ? 'Weekly scans on' : 'Weekly scans off',
        body: !weeklyOn ? undefined : 'Automatic scans disabled.',
      });
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not update the setting', body: (e && e.message) || '' });
    }
  };

  // Keep the ORIGINAL row index — actions address rows by their stored
  // position, not their filtered position.
  const rows = ((results && results.rows) || []).map((r, i) => ({ ...r, _i: i }));
  // Active/All read the selected run; Checked/Dismissed/Imported read the
  // registry (global across runs). Registry entries reshape into table rows
  // via their snapshot — pre-snapshot entries fall back to email + category.
  const registryTab = statusFilter === 'checked' || statusFilter === 'dismissed' || statusFilter === 'imported';
  const regRows = registryTab
    ? (registry || []).filter((e) => e.status === statusFilter).map((e) => ({
        contact_email: e.correspondent_email,
        ...(e.snapshot || {}),
        category: e.category, status: e.status, feedback: e.feedback,
        acted_by: e.acted_by, acted_at: e.acted_at, _registry: true,
      }))
    : [];
  const byStatus = registryTab ? regRows
    : statusFilter === 'active' ? rows.filter((r) => !r.status) : rows;
  const filtered = relevanceFilter === 'all' ? byStatus : byStatus.filter((r) => r.deal_relevance === relevanceFilter);
  const relevances = Array.from(new Set(byStatus.map((r) => r.deal_relevance))).filter(Boolean);
  const nChecked = registry ? registry.filter((e) => e.status === 'checked').length : null;
  const nDismissed = registry ? registry.filter((e) => e.status === 'dismissed').length : null;
  const nImported = registry ? registry.filter((e) => e.status === 'imported').length : null;

  const runAction = async (row, body, successToast) => {
    setActionBusy(true);
    try {
      const res = await window.AtlasApi.lostDealsRowAction(selectedId, row._i, body);
      setResults((prev) => {
        if (!prev) return prev;
        const next = [...prev.rows];
        next[row._i] = res.row;
        return { ...prev, rows: next };
      });
      if (successToast) pushToast(successToast);
      setNoteTarget(null);
      loadRegistry();      // acted-on rows land on their registry tab at once
      return res.row;
    } catch (e) {
      let msg = (e && e.message) || 'Unknown error';
      try { const m = msg.match(/\{.*\}$/s); const p = m && JSON.parse(m[0]); if (p && p.detail) msg = p.detail; } catch (_) {}
      pushToast({ type: 'error', title: 'Action failed', body: msg });
      return null;
    } finally {
      setActionBusy(false);
    }
  };

  const importRow = async (row) => {
    const updated = await runAction(row, { action: 'import' }, null);
    if (updated) {
      pushToast({
        type: 'success',
        title: 'Added to Atlas',
        body: `${updated.company_guess || updated.contact_email} imported${updated.import_created && updated.import_created.company ? ' (new company)' : ''} — a summary note with the Outlook link is on the record.`,
      });
    }
  };

  // Deep-history scans return up to 1,000 findings — paginate the table.
  const PAGE = 100;
  const [page, setPage] = React.useState(0);
  React.useEffect(() => { setPage(0); }, [selectedId, relevanceFilter, statusFilter]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE));
  const pageRows = filtered.slice(page * PAGE, (page + 1) * PAGE);

  const exportRows = () => {
    // Registry tabs can be exported with no run loaded — derive columns
    // from the rows themselves (skipping the _-prefixed UI bookkeeping).
    const keys = (results && results.columns && results.columns.length && !registryTab)
      ? results.columns
      : Array.from(new Set(filtered.flatMap((r) => Object.keys(r)))).filter((k) => !k.startsWith('_'));
    window.exportCsv('lost_deals.csv', keys.map((c) => ({ key: c, label: c })), filtered);
  };

  const selectedRun = (runs || []).find((r) => r.id === selectedId);

  const cellHead = { padding: '7px 10px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.04em', textAlign: 'left', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: 'var(--surface)', borderBottom: '1px solid var(--border)', zIndex: 1 };
  const cell = { padding: '7px 10px', fontSize: 12, color: 'var(--text-2)', verticalAlign: 'top', borderBottom: '1px solid var(--border)' };

  // Shared by the run-scoped tabs (Active / All) and the registry-backed
  // tabs (Checked / Dismissed / Imported) — the latter render with no run
  // selected, and their rows may predate snapshots (hence the — fallbacks).
  const findingsBody = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {!registryTab && selectedRun && selectedRun.output_summary && (
        <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{selectedRun.output_summary}</div>
      )}
      {!filtered.length ? (
        <div style={{ border: '1px dashed var(--border-strong)', borderRadius: 10, padding: '24px', maxWidth: 640, fontSize: 12.5, color: 'var(--text-3)' }}>
          {registryTab
            ? `Nothing ${statusFilter} yet — use the ✓ (checked), Add or ✕ (false positive) actions on active findings; they stay here across scans.`
            : `Nothing matched${relevanceFilter !== 'all' ? ' this relevance filter' : ' — inbox looks fully tracked for this window'}.`}
        </div>
      ) : (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'auto', maxHeight: 'calc(100vh - 220px)' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 980 }}>
            <thead>
              <tr>
                {['Relevance', 'Category', 'Company', 'Contact', 'In CRM', 'Summary', 'Suggested action', 'Silent', 'Threads', 'Msgs', '', registryTab ? 'Resolution' : 'Actions'].map((h, hi) => (
                  <th key={hi} style={cellHead}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r, i) => (
                <tr key={i} style={{ background: 'var(--surface)' }}>
                  <td style={cell}>
                    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                      <Pill hue={LD_RELEVANCE_HUES[r.deal_relevance] || 'slate'} sz="sm">{ldLabel(r.deal_relevance) || '—'}</Pill>
                      {r.is_new && <Pill hue="green" dot sz="sm">new</Pill>}
                    </span>
                    {r.confidence && <div style={{ fontSize: 10, color: 'var(--text-4)', marginTop: 2 }}>{r.confidence}</div>}
                  </td>
                  <td style={cell}>{LD_CATEGORY_LABELS[r.category] || ldLabel(r.category)}</td>
                  <td style={{ ...cell, fontWeight: 600, color: 'var(--text)' }}>{r.company_guess || '—'}</td>
                  <td style={cell}>
                    <div>{r.contact_name || '—'}</div>
                    {r.contact_email && <div className="mono" style={{ fontSize: 10.5, color: 'var(--text-3)' }}>{r.contact_email}</div>}
                  </td>
                  <td style={cell}>
                    {r.in_crm ? <Pill hue={r.in_crm === 'none' ? 'rose' : 'slate'} sz="sm">{r.in_crm}</Pill> : '—'}
                    {r.has_engagement && <div style={{ fontSize: 10, color: 'var(--text-4)', marginTop: 2 }}>has engagement</div>}
                    {r.possible_crm_match && (
                      <div title={r.possible_crm_match} style={{ marginTop: 3 }}>
                        <Pill hue="amber" sz="sm">in Atlas?</Pill>
                      </div>
                    )}
                  </td>
                  <td style={{ ...cell, maxWidth: 320 }}>
                    {r.summary || r.subject}
                    {r.mentioned_financials && (
                      <div style={{ fontSize: 11, color: 'var(--amber-fg, #b45309)', marginTop: 2 }} className="num">
                        {r.mentioned_financials}
                      </div>
                    )}
                  </td>
                  <td style={{ ...cell, maxWidth: 220 }}>{r.suggested_action || '—'}</td>
                  <td style={{ ...cell, whiteSpace: 'nowrap' }} className="num">{r.days_silent != null ? `${r.days_silent}d` : '—'}</td>
                  <td style={{ ...cell, whiteSpace: 'nowrap' }} className="num">{r.conversations || 1}</td>
                  <td style={{ ...cell, whiteSpace: 'nowrap' }} className="num">{r.messages != null ? `${r.messages} (${r.our_replies} ours)` : '—'}</td>
                  <td style={cell}>
                    {r.outlook_link && (
                      <a href={r.outlook_link} target="_blank" rel="noreferrer" title="Open in Outlook"
                        style={{ color: 'var(--accent)', display: 'inline-flex' }}><Icon.External size={12} /></a>
                    )}
                  </td>
                  <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                    {!r.status && !r._registry && (
                      <span style={{ display: 'inline-flex', gap: 4 }}>
                        {r.draft_reply && (
                          <Btn size="xs" variant="outline" title="View the proposed follow-up reply"
                            onClick={() => setDraftTarget(r)}>Draft</Btn>
                        )}
                        <Btn size="xs" variant="outline" disabled={actionBusy}
                          title="Create the company + contact in Atlas with a summary note"
                          onClick={() => importRow(r)}>Add</Btn>
                        <Btn size="xs" variant="ghost" disabled={actionBusy}
                          title="Mark checked — real finding, already handled (asks for a note)"
                          onClick={() => setNoteTarget({ row: r, action: 'check' })}>✓</Btn>
                        <Btn size="xs" variant="ghost" disabled={actionBusy}
                          title="Mark as false positive (asks why)"
                          onClick={() => setNoteTarget({ row: r, action: 'dismiss' })}>✕</Btn>
                      </span>
                    )}
                    {r.status === 'checked' && (
                      <span title={r.feedback ? `${r.acted_by}: ${r.feedback}` : ''}>
                        <Pill hue="teal" sz="sm">checked</Pill>
                      </span>
                    )}
                    {r.status === 'dismissed' && (
                      <span title={r.feedback ? `${r.acted_by}: ${r.feedback}` : ''}>
                        <Pill hue="rose" sz="sm">dismissed</Pill>
                      </span>
                    )}
                    {r.status === 'imported' && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Pill hue="green" sz="sm">imported</Pill>
                        {r.imported_company_id && goToCompany && (
                          <button onClick={() => goToCompany(r.imported_company_id)}
                            title="Open the company (create an engagement from there)"
                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--accent)', fontSize: 11.5, padding: 0 }}>
                            open
                          </button>
                        )}
                      </span>
                    )}
                    {r._registry && (r.feedback || r.acted_by) && (
                      <div style={{ fontSize: 10.5, color: 'var(--text-3)', maxWidth: 220, marginTop: 3, whiteSpace: 'normal' }}>
                        {r.feedback}
                        {r.acted_by && (
                          <span style={{ color: 'var(--text-4)' }}>
                            {r.feedback ? ' — ' : ''}{r.acted_by}{r.acted_at ? `, ${window.relativeTime ? window.relativeTime(r.acted_at) : r.acted_at}` : ''}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {pageCount > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end', fontSize: 12, color: 'var(--text-3)' }}>
          <Btn size="xs" variant="outline" disabled={page === 0} onClick={() => setPage(page - 1)}>Prev</Btn>
          <span className="num">{page * PAGE + 1}–{Math.min((page + 1) * PAGE, filtered.length)} of {filtered.length}</span>
          <Btn size="xs" variant="outline" disabled={page >= pageCount - 1} onClick={() => setPage(page + 1)}>Next</Btn>
        </div>
      )}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        breadcrumb={['Workspace', 'Agents', 'Lost Deals Finder']}
        title="Lost Deals Finder"
        actions={(
          <>
            {msFeatures !== null && (
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-2)', cursor: 'pointer', marginRight: 4 }}>
                <input type="checkbox" checked={weeklyOn} onChange={toggleWeekly} style={{ accentColor: 'var(--accent)' }} />
                Weekly scan
              </label>
            )}
            <window.Picker value={customRange ? 'custom' : days} placeholder="Window"
              onChange={(v) => {
                if (v === 'custom') { setCustomRange(true); return; }
                setCustomRange(false); setDays(Number(v));
              }}
              options={[
                { value: 30, label: 'Last 30 days' }, { value: 90, label: 'Last 90 days' }, { value: 180, label: 'Last 180 days' },
                { value: 365, label: 'Last 12 months' }, { value: 730, label: 'Last 2 years' }, { value: 1825, label: 'Last 5 years' },
                { value: 'custom', label: 'Custom range…' },
              ]} />
            {customRange && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input type="date" value={sinceDate} onChange={(e) => setSinceDate(e.target.value)}
                  title="From (required)"
                  style={{ height: 28, fontSize: 12, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text)', padding: '0 6px' }} />
                <span style={{ fontSize: 11, color: 'var(--text-4)' }}>→</span>
                <input type="date" value={untilDate} onChange={(e) => setUntilDate(e.target.value)}
                  title="To (blank = now)"
                  style={{ height: 28, fontSize: 12, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text)', padding: '0 6px' }} />
              </span>
            )}
            <Btn size="sm" variant="accent" icon={<Icon.Play size={12} />} disabled={starting || inFlight}
              onClick={startRun}>{inFlight ? 'Scanning…' : 'Run scan'}</Btn>
          </>
        )}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 16px', borderBottom: '1px solid var(--border)', background: 'var(--surface)', height: 36 }}>
        {[['results', 'Results', results ? filtered.length : null], ['runs', 'Runs', runs ? runs.length : null]].map(([k, label, count]) => {
          const active = tab === k;
          return (
            <button key={k} onClick={() => setTab(k)} style={{
              padding: '0 10px', height: 36, border: 'none', background: 'transparent',
              fontSize: 12.5, fontWeight: active ? 600 : 500, cursor: 'pointer',
              color: active ? 'var(--text)' : 'var(--text-3)',
              borderBottom: active ? '2px solid var(--text)' : '2px solid transparent',
              marginBottom: -1, display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>{label}{count != null && <span style={{ fontSize: 10.5, color: 'var(--text-4)' }} className="num">{count}</span>}</button>
          );
        })}
        <div style={{ flex: 1 }} />
        {tab === 'results' && (
          <>
            <span style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
              {[['active', 'Active', null], ['checked', 'Checked', nChecked], ['dismissed', 'Dismissed', nDismissed],
                ['imported', 'Imported', nImported], ['all', 'All'], ].map(([k, label, count], i) => (
                <button key={k} onClick={() => setStatusFilter(k)} style={{
                  height: 24, padding: '0 8px', fontSize: 11.5, cursor: 'pointer',
                  border: 'none', borderLeft: i === 0 ? 'none' : '1px solid var(--border)',
                  background: statusFilter === k ? 'var(--surface-hover)' : 'var(--surface)',
                  color: statusFilter === k ? 'var(--text)' : 'var(--text-3)',
                  fontWeight: statusFilter === k ? 600 : 500,
                }}>{label}{count ? ` ${count}` : ''}</button>
              ))}
            </span>
            <window.Picker value={relevanceFilter} onChange={setRelevanceFilter} size="sm" placeholder="Relevance"
              options={[{ value: 'all', label: 'All relevance' }, ...relevances.map((r) => ({ value: r, label: ldLabel(r) }))]} />
            <Btn size="xs" variant="outline" icon={<Icon.External size={11} />} onClick={exportRows}>CSV</Btn>
          </>
        )}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 16, background: 'var(--surface-2)' }}>
        {tab === 'results' && (
          // Registry tabs are global — they render regardless of run state.
          registryTab ? (!registry ? <window.SkeletonRows rows={5} /> : findingsBody)
          : !runs ? <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>
          : !runs.length ? (
            <div style={{ border: '1px dashed var(--border-strong)', borderRadius: 10, padding: '32px 24px', maxWidth: 640, fontSize: 12.5, color: 'var(--text-3)' }}>
              No scans yet. Pick a window and hit <b>Run scan</b>.
            </div>
          )
          : !selectedId ? (
            <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
              {inFlight ? 'Scan in progress — results will appear here when it finishes.' : 'No completed scan yet — check the Runs tab.'}
            </div>
          )
          : !results ? <window.SkeletonRows rows={5} />
          : findingsBody
        )}

        {tab === 'runs' && (
          !runs ? <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>
          : !runs.length ? <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>No runs yet.</div>
          : (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', maxWidth: 860 }}>
              {runs.map((r, i) => (
                <div key={r.id} onClick={() => { setSelectedId(r.id); setTab('results'); }} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer',
                  borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                  background: r.id === selectedId ? 'var(--surface-hover)' : 'transparent',
                }}>
                  <Pill sz="sm" dot hue={r.status === 'completed' ? 'green' : r.status === 'failed' ? 'rose' : 'amber'}>{r.status}</Pill>
                  <span style={{ fontSize: 12, color: 'var(--text-2)' }} className="num">
                    {r.options && r.options.since
                      ? `${r.options.since} → ${r.options.until || 'now'}`
                      : `${(r.options && r.options.days) || '—'}d window`}{r.row_count != null ? ` · ${r.row_count} findings` : ''}
                  </span>
                  <span style={{ flex: 1, fontSize: 11.5, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.output_summary || ''}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text-4)' }}>{window.relativeTime ? window.relativeTime(r.created_at) : r.created_at}</span>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {draftTarget && <LdDraftModal row={draftTarget} onClose={() => setDraftTarget(null)} />}
      {noteTarget && (
        <LdNoteModal row={noteTarget.row} action={noteTarget.action} busy={actionBusy}
          onCancel={() => setNoteTarget(null)}
          onConfirm={(reason) => runAction(noteTarget.row, { action: noteTarget.action, reason },
            noteTarget.action === 'check'
              ? { type: 'success', title: 'Checked off', body: 'Moved to the Checked tab — it stays there across future scans.' }
              : { type: 'success', title: 'Marked false positive', body: 'Saved to the run workspace for admin review.' })} />
      )}
    </div>
  );
};

window.LostDealsView = LostDealsView;
